/*
 * VAE Latent Space Explorer: teaching simulator (Case Study, HL only)
 * ------------------------------------------------------------------------------
 * WHAT IT TEACHES (broad understanding only)
 *   A variational autoencoder (VAE) represents images in a latent space: a
 *   compressed, organised space where similar images sit close together, and
 *   moving through the space morphs the output.
 *     • A few anchor images sit at fixed points in the space.
 *     • Near an anchor the output resembles it.
 *     • Between anchors the output is a blend.
 *     • Closeness in the space means similarity of the images.
 *
 * This is an ILLUSTRATION, not real machine learning. Nothing is trained, no
 * dataset is loaded, no library is used. The "latent space" is a 2D pad; the
 * output is a simple parametric face whose parameters are interpolated between
 * the anchors by distance (inverse-distance weighting). No real VAE.
 *
 * CONTROLS
 *   • 2D pad (drag / arrow keys) .... move a point through the latent space; the
 *                                     output updates live.
 *   • Latent dimension 1 / 2 (sliders)  a keyboard-friendly alternative to the pad.
 *   • Explainer accordion ........... what a latent space is.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';

// ── brookebit palette (shared with the other simulators) ───────────────────────
const B = {
  bg:           '#faf7f2',
  surface:      '#ffffff',
  border:       'rgba(60,45,30,0.10)',
  borderStrong: 'rgba(60,45,30,0.16)',
  text:         '#2a241d',
  muted:        '#6b6055',
  faint:        '#9a8f82',
  green:        '#7BC19E',
  greenDark:    '#5fa882',
  greenSoft:    '#eef6f1',
  charcoal:     '#3A3D3C',
  blue:         '#5a7abf',
  purple:       '#7a5bbf',
  purpleSoft:   '#efe7fb',
};

const font = "'Nunito', -apple-system, sans-serif";
const mono = "'JetBrains Mono', 'Fira Mono', monospace";

// ── Anchors: a few images at fixed points in the latent space ──────────────────
// Positions are unit coordinates (0..1) on the pad. The params are interpolated
// between anchors. Happy faces cluster at the top, sad at the bottom, so
// "closeness in the space = similarity of the images" is visible.
const ANCHORS = [
  { id: 'a', name: 'Happy blue',    x: 0.16, y: 0.16, p: { hue: 205, width: 0.55, eye: 0.72, mouth:  0.85, ear: 0.35, round: 0.60 } },
  { id: 'b', name: 'Happy green',   x: 0.84, y: 0.16, p: { hue: 135, width: 0.62, eye: 0.85, mouth:  0.70, ear: 0.55, round: 0.55 } },
  { id: 'c', name: 'Sad purple',    x: 0.16, y: 0.84, p: { hue: 262, width: 0.50, eye: 0.45, mouth: -0.75, ear: 0.30, round: 0.72 } },
  { id: 'd', name: 'Grumpy orange', x: 0.84, y: 0.84, p: { hue:  26, width: 0.72, eye: 0.52, mouth: -0.85, ear: 0.62, round: 0.50 } },
  { id: 'e', name: 'Calm pink',     x: 0.50, y: 0.50, p: { hue: 322, width: 0.60, eye: 0.66, mouth:  0.08, ear: 0.45, round: 0.60 } },
];

// Inverse-distance-weighted blend of the anchor parameters at point (x, y).
function interpolate(x, y) {
  let wSum = 0;
  const acc = { hue: 0, width: 0, eye: 0, mouth: 0, ear: 0, round: 0 };
  const weights = [];
  for (const a of ANCHORS) {
    const d2 = (x - a.x) ** 2 + (y - a.y) ** 2;
    const w = 1 / (d2 + 0.0009);          // small eps: right on an anchor, it dominates
    weights.push(w);
    wSum += w;
    for (const k in acc) acc[k] += w * a.p[k];
  }
  for (const k in acc) acc[k] /= wSum;
  return { params: acc, weights: weights.map((w) => w / wSum) };
}

// ── Draw a parametric face from an interpolated parameter vector ───────────────
function drawFace(ctx, p, W, H) {
  ctx.clearRect(0, 0, W, H);
  // Background: a soft wash tinted by the face hue
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, `hsl(${p.hue}, 40%, 95%)`);
  bg.addColorStop(1, `hsl(${p.hue}, 30%, 88%)`);
  ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

  const cx = W / 2, cy = H * 0.54;
  const rx = W * (0.20 + 0.14 * p.width);
  const ry = H * (0.22 + 0.12 * p.round);
  const faceFill = `hsl(${p.hue}, 58%, 62%)`;
  const faceEdge = `hsl(${p.hue}, 50%, 40%)`;

  // Ears
  const earH = H * (0.05 + 0.16 * p.ear);
  ctx.fillStyle = faceFill; ctx.strokeStyle = faceEdge; ctx.lineWidth = 2; ctx.lineJoin = 'round';
  [-1, 1].forEach((s) => {
    const ex = cx + s * rx * 0.62;
    ctx.beginPath();
    ctx.moveTo(ex - rx * 0.22, cy - ry * 0.72);
    ctx.lineTo(ex, cy - ry * 0.72 - earH);
    ctx.lineTo(ex + rx * 0.22, cy - ry * 0.72);
    ctx.closePath(); ctx.fill(); ctx.stroke();
  });

  // Face
  ctx.beginPath(); ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
  ctx.fillStyle = faceFill; ctx.fill(); ctx.strokeStyle = faceEdge; ctx.stroke();

  // Eyes
  const eyeR = Math.max(3, W * (0.02 + 0.05 * p.eye));
  const eyeDX = rx * 0.44, eyeY = cy - ry * 0.12;
  [-1, 1].forEach((s) => {
    ctx.beginPath(); ctx.arc(cx + s * eyeDX, eyeY, eyeR, 0, Math.PI * 2);
    ctx.fillStyle = '#fbf9f4'; ctx.fill(); ctx.strokeStyle = faceEdge; ctx.lineWidth = 1.5; ctx.stroke();
    ctx.beginPath(); ctx.arc(cx + s * eyeDX + eyeR * 0.15, eyeY, eyeR * 0.5, 0, Math.PI * 2);
    ctx.fillStyle = '#2a241d'; ctx.fill();
  });

  // Mouth: mouth = +1 smile (U), -1 frown; canvas y is down
  const mw = rx * 0.52, my = cy + ry * 0.42;
  const curve = p.mouth * ry * 0.42;
  ctx.beginPath();
  ctx.moveTo(cx - mw, my);
  ctx.quadraticCurveTo(cx, my + curve, cx + mw, my);
  ctx.strokeStyle = faceEdge; ctx.lineWidth = 3.5; ctx.lineCap = 'round'; ctx.stroke();
}

// A small fixed anchor thumbnail (drawn once).
function AnchorThumb({ params, size }) {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    c.width = size; c.height = size;
    drawFace(c.getContext('2d'), params, size, size);
  }, [params, size]);
  return <canvas ref={ref} style={{ width: size, height: size, display: 'block', borderRadius: '50%' }} />;
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function VAELatentSpaceSimulator({ onBack }) {
  const outRef = useRef(null);
  const padRef = useRef(null);
  const draggingRef = useRef(false);

  const [pos, setPos] = useState({ x: 0.16, y: 0.16 });   // start on the "Happy blue" anchor
  const [hintOpen, setHintOpen] = useState(false);

  const { params, weights } = interpolate(pos.x, pos.y);

  // Render the live output whenever the point moves
  useEffect(() => {
    const c = outRef.current; if (!c) return;
    const S = 260; c.width = S; c.height = S;
    drawFace(c.getContext('2d'), params, S, S);
  }, [params]);

  // ── Pad interaction ──────────────────────────────────────────────────────────
  const setFromEvent = useCallback((e) => {
    const rect = padRef.current.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
    setPos({ x, y });
  }, []);
  const onPointerDown = (e) => {
    e.preventDefault();
    draggingRef.current = true;
    padRef.current.setPointerCapture?.(e.pointerId);
    padRef.current.focus();          // preventDefault blocks default focus; focus explicitly for arrow keys
    setFromEvent(e);
  };
  const onPointerMove = (e) => { if (draggingRef.current) setFromEvent(e); };
  const onPointerUp = () => { draggingRef.current = false; };
  const onKeyDown = (e) => {
    const step = 0.03;
    const moves = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
    const m = moves[e.key];
    if (!m) return;
    e.preventDefault();
    setPos((prev) => ({ x: Math.min(1, Math.max(0, prev.x + m[0])), y: Math.min(1, Math.max(0, prev.y + m[1])) }));
  };

  // Nearest / second-nearest anchor for the readout
  const ranked = ANCHORS
    .map((a, i) => ({ a, w: weights[i], d: Math.hypot(pos.x - a.x, pos.y - a.y) }))
    .sort((m, n) => n.w - m.w);
  const near = ranked[0];
  const resembles = near.d < 0.14;
  const readout = resembles
    ? `Resembles “${near.a.name}”`
    : `Blend of “${ranked[0].a.name}” and “${ranked[1].a.name}”`;

  // ── Styles ───────────────────────────────────────────────────────────────────
  const s = {
    wrap: { fontFamily: font, background: B.bg, minHeight: '100vh', WebkitFontSmoothing: 'antialiased' },
    topbar: {
      position: 'sticky', top: 0, zIndex: 100, background: B.surface,
      borderBottom: `1px solid ${B.border}`, display: 'flex', alignItems: 'center',
      padding: '8px 1.5rem', minHeight: 60, gap: '0.75rem', flexWrap: 'wrap',
    },
    backBtn: {
      background: 'none', border: 'none', cursor: 'pointer', color: B.muted,
      fontFamily: font, fontSize: '0.85rem', fontWeight: 600, display: 'flex',
      alignItems: 'center', gap: 5, padding: '4px 8px', borderRadius: 6,
    },
    title: { fontSize: '1rem', fontWeight: 700, color: B.text, margin: 0 },
    badge: { fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.04em', background: B.greenSoft, color: B.greenDark, padding: '3px 8px', borderRadius: 6 },
    lvlBadge: { fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.06em', background: B.purpleSoft, color: B.purple, padding: '3px 7px', borderRadius: 6 },
    body: { maxWidth: 820, margin: '0 auto', padding: '2rem 1.5rem 3rem' },
    card: { background: B.surface, border: `1px solid ${B.border}`, borderRadius: 14, padding: '1.25rem', marginBottom: '1rem' },
    sectionLabel: { fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em', color: B.faint, textTransform: 'uppercase', marginBottom: '0.6rem' },
    grid: { display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'flex-start' },
    col: { flex: '1 1 260px', minWidth: 240 },
    pad: {
      position: 'relative', width: '100%', maxWidth: 300, aspectRatio: '1',
      background: `radial-gradient(circle at 50% 50%, #ffffff, ${B.bg})`,
      border: `1.5px solid ${B.borderStrong}`, borderRadius: 12, margin: '0 auto',
      touchAction: 'none', cursor: 'crosshair', outline: 'none',
    },
    handle: {
      position: 'absolute', width: 26, height: 26, borderRadius: '50%',
      border: '3px solid #fff', boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
      transform: 'translate(-50%, -50%)', pointerEvents: 'none',
    },
    anchorWrap: { position: 'absolute', transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none' },
    anchorRing: { borderRadius: '50%', border: `2px solid ${B.surface}`, boxShadow: '0 1px 3px rgba(0,0,0,0.18)' },
    anchorLabel: { fontSize: '0.6rem', fontWeight: 700, color: B.muted, marginTop: 2, whiteSpace: 'nowrap' },
    outCanvas: { width: '100%', maxWidth: 300, aspectRatio: '1', display: 'block', margin: '0 auto', borderRadius: 12, border: `1px solid ${B.border}`, background: B.bg },
    ctrlLabel: { fontSize: '0.72rem', fontWeight: 700, color: B.muted, textTransform: 'uppercase', letterSpacing: '0.04em' },
    readout: { fontFamily: mono, fontSize: '0.9rem', fontWeight: 800, color: B.purple },
    expBtn: { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'none', border: 'none', cursor: 'pointer', fontFamily: font, fontSize: '0.9rem', fontWeight: 600, color: B.text, padding: '0.9rem 1.25rem', borderRadius: 14 },
    expBody: { padding: '0 1.25rem 1.25rem', borderTop: `1px solid ${B.border}` },
    expGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '0.9rem' },
    expCard: { padding: '0.9rem', borderRadius: 8, border: `1px solid ${B.border}` },
    expCardTitle: { fontSize: '0.85rem', fontWeight: 700, color: B.text, marginBottom: '0.5rem' },
    expList: { fontSize: '0.8rem', color: B.muted, lineHeight: 1.65, paddingLeft: '1rem', margin: 0 },
    expNote: { fontSize: '0.78rem', color: B.faint, lineHeight: 1.6, marginTop: '0.75rem' },
  };

  const ANCHOR_SIZE = 46;

  return (
    <div style={s.wrap}>
      {/* ── Topbar ─────────────────────────────────────────────────────── */}
      <header style={s.topbar}>
        {onBack && (
          <button style={s.backBtn} onClick={onBack} aria-label="Back">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="10 3 5 8 10 13" />
            </svg>
            Back
          </button>
        )}
        <a href="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 }}>
          <img src="/logo.svg" alt="brookebit" height={40} style={{ display: 'block' }} />
        </a>
        <h1 style={s.title}>VAE Latent Space</h1>
        <span style={s.badge}>Case Study</span>
        <span style={s.lvlBadge}>HL</span>
      </header>

      {/* ── Body ───────────────────────────────────────────────────────── */}
      <main style={s.body}>

        <div style={s.card}>
          <div style={s.grid}>
            {/* Pad (latent space) */}
            <div style={s.col}>
              <div style={s.sectionLabel}>Latent space (drag the point)</div>
              <div
                ref={padRef}
                style={s.pad}
                tabIndex={0}
                role="application"
                aria-label="Latent space pad. Drag, or use the arrow keys, to move a point through the space. The image on the right updates to match."
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerLeave={onPointerUp}
                onKeyDown={onKeyDown}
              >
                {ANCHORS.map((a) => (
                  <div key={a.id} style={{ ...s.anchorWrap, left: `${a.x * 100}%`, top: `${a.y * 100}%` }}>
                    <div style={s.anchorRing}>
                      <AnchorThumb params={a.p} size={ANCHOR_SIZE} />
                    </div>
                    <div style={s.anchorLabel}>{a.name}</div>
                  </div>
                ))}
                <div style={{ ...s.handle, left: `${pos.x * 100}%`, top: `${pos.y * 100}%`, background: `hsl(${params.hue}, 58%, 55%)` }} />
              </div>
            </div>

            {/* Live output */}
            <div style={s.col}>
              <div style={s.sectionLabel}>Generated image</div>
              <canvas ref={outRef} style={s.outCanvas} role="img"
                aria-label={`Generated face at latent position ${pos.x.toFixed(2)}, ${pos.y.toFixed(2)}. ${readout}.`} />
              <div style={{ textAlign: 'center', marginTop: '0.7rem' }}>
                <div style={s.readout}>{readout}</div>
                <div style={{ fontSize: '0.72rem', color: B.faint, marginTop: 2 }}>
                  latent = ({pos.x.toFixed(2)}, {pos.y.toFixed(2)})
                </div>
              </div>
            </div>
          </div>

          {/* Sliders: keyboard-friendly alternative to the pad */}
          <div style={{ display: 'flex', gap: '1.6rem', flexWrap: 'wrap', marginTop: '1.2rem', borderTop: `1px solid ${B.border}`, paddingTop: '1rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={s.ctrlLabel} htmlFor="dim1">Latent dimension 1 (horizontal)</label>
              <input id="dim1" type="range" min="0" max="100" value={Math.round(pos.x * 100)}
                onChange={(e) => setPos((p) => ({ ...p, x: +e.target.value / 100 }))}
                style={{ accentColor: B.green, width: 200 }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={s.ctrlLabel} htmlFor="dim2">Latent dimension 2 (vertical)</label>
              <input id="dim2" type="range" min="0" max="100" value={Math.round(pos.y * 100)}
                onChange={(e) => setPos((p) => ({ ...p, y: +e.target.value / 100 }))}
                style={{ accentColor: B.green, width: 200 }} />
            </div>
          </div>

          <p style={{ fontSize: '0.78rem', color: B.muted, marginTop: '0.9rem', lineHeight: 1.55 }}>
            <strong>Try it:</strong> drag the point onto an anchor and the output becomes that image; drag
            between anchors and it becomes a <strong>blend</strong>. Notice the happy faces cluster at the
            top and the sad faces at the bottom: in a <strong>latent space</strong>, similar images sit close together.
          </p>
        </div>

        {/* ── Explainer accordion ────────────────────────────────────── */}
        <div style={{ ...s.card, padding: 0, overflow: 'hidden' }}>
          <button style={s.expBtn} onClick={() => setHintOpen((o) => !o)} aria-expanded={hintOpen}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke={B.green}
                strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M6 2h4M8 2v3M4.5 5.5A4 4 0 1 0 11.5 5.5" />
                <line x1="8" y1="9" x2="8" y2="12" />
                <circle cx="8" cy="13.5" r="0.5" fill={B.green} stroke="none" />
              </svg>
              What is a latent space?
            </span>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke={B.green}
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
              style={{ transform: hintOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
              <polyline points="3 6 8 11 13 6" />
            </svg>
          </button>

          {hintOpen && (
            <div style={s.expBody}>
              <p style={{ fontSize: '0.85rem', color: B.text, lineHeight: 1.6, marginTop: '0.9rem' }}>
                A <strong>variational autoencoder (VAE)</strong> learns to compress images into a small,
                organised <strong>latent space</strong>. Every point in that space maps to an image, and the
                space is arranged so that <strong>similar images sit close together</strong>. Moving smoothly
                through the space morphs the output smoothly.
              </p>
              <div style={s.expGrid}>
                <div style={s.expCard}>
                  <div style={s.expCardTitle}>What to notice</div>
                  <ul style={s.expList}>
                    <li>Each anchor is a fixed image at a fixed point.</li>
                    <li><strong>Near</strong> an anchor, the output <strong>resembles</strong> it.</li>
                    <li><strong>Between</strong> anchors, the output is a <strong>blend</strong>.</li>
                  </ul>
                </div>
                <div style={s.expCard}>
                  <div style={s.expCardTitle}>Why it matters</div>
                  <ul style={s.expList}>
                    <li>Distance in the space reflects <strong>similarity</strong> of the images.</li>
                    <li>A whole family of images is captured by just <strong>two numbers</strong> here.</li>
                    <li>This organised space is what lets a model generate and interpolate.</li>
                  </ul>
                </div>
              </div>
              <p style={s.expNote}>
                Here the latent space is only 2D so it fits on screen; a real VAE uses many dimensions. The
                key idea is the same: a <strong>compressed, organised space</strong> where closeness means
                similarity and movement means a smooth change in the image.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
